import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useMedia } from 'react-use'
import mergeRefs from 'merge-refs'
import Image from 'next/image'

import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'

import Container from '../base/container'
import { hasVideo } from '../base/media'
import { useFullscreenMediaMotion } from './motion'
import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

type FullscreenMediaProps = HTMLAttributes<HTMLDivElement> & {
  media: WPMedia
}

const FullscreenMedia = ({ media, className, ...props }: FullscreenMediaProps) => {
  const root = useRef<HTMLDivElement>(null)
  const cursor = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null!)
  const { motionRef } = useFullscreenMediaMotion()
  const refs = mergeRefs(root, motionRef)
  const player = useRef<Plyr>(null!)

  const [isPlaying, setIsPlaying] = useState(false)

  const timeoutID = useRef<NodeJS.Timeout | null>(null)
  const isMobile = useMedia('(max-width: 767px)', false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('plyr').then((PlyrModule) => {
        player.current = new PlyrModule.default(videoRef.current, {})

        player.current.on('playing', () => {
          setIsPlaying(true)
          if (timeoutID.current) {
            clearTimeout(timeoutID.current)
          }
        })
        player.current.on('pause', () => {
          timeoutID.current = setTimeout(() => {
            setIsPlaying(false)
          }, 1000)
        })
      })
    }
  }, [])

  return media.poster?.url || media.video?.url ? (
    <Section
      ref={refs}
      className={cn('relative z-10 [--plyr-color-main:#1F9E9E]', className)}
      data-fullscreen-media
      {...props}
    >
      <Container data-motion-block>
        <div className="relative">
          {hasVideo(media) ? (
            <div
              ref={cursor}
              className={cn(
                'pointer-events-none absolute z-30 rounded-radius-minimal',
                'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'border-[0.2rem] border-white-light p-spacing-sm',
                'flex items-center justify-center',
                'transition-opacity duration-1000',
                isPlaying ? 'opacity-0' : 'opacity-100'
              )}
            >
              <SVGIcon
                symbol="play-icon"
                className={cn(
                  'fill-white-light',
                  'size-[2.4rem]',
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
                )}
              />
            </div>
          ) : null}

          {hasVideo(media) ? (
            <div className="aspect-video w-full">
              {media.poster?.url && (
                <Image
                  src={media.poster.url}
                  width={media.poster.width}
                  height={media.poster.height}
                  draggable={false}
                  alt={media.poster.alt || 'Poster Image'}
                  className={cn(
                    'user-select-none absolute left-0 top-0 z-20 h-full w-full cursor-pointer object-cover transition-opacity duration-1000',
                    isPlaying ? 'pointer-events-none opacity-0' : 'opacity-100'
                  )}
                  onClick={() => {
                    videoRef.current.play()
                  }}
                  style={{ transition: 'opacity 0.3s' }}
                  priority={true}
                  fetchPriority="high"
                  loading="eager"
                />
              )}
              <video
                ref={videoRef}
                className="h-full w-full object-cover"
                data-poster={media.poster?.url}
              >
                <source src={media.video?.url} type="video/mp4" />
              </video>
            </div>
          ) : (
            media.poster?.url && (
              <>
                <Image
                  src={
                    isMobile && media.poster_mobile?.url
                      ? media.poster_mobile.url
                      : media.poster.url
                  }
                  width={
                    isMobile && media.poster_mobile?.url
                      ? media.poster_mobile.width
                      : media.poster.width
                  }
                  height={
                    isMobile && media.poster_mobile?.url
                      ? media.poster_mobile.height
                      : media.poster.height
                  }
                  alt={media.poster.alt || 'Poster Image'}
                  className="user-select-none pointer-events-none h-auto w-full"
                  fetchPriority="high"
                  loading="eager"
                />
              </>
            )
          )}
        </div>
      </Container>
    </Section>
  ) : null
}

export default FullscreenMedia
